import { SagaNodeStatus, sagaNodeStatusColor, type SagaNodeKind as SagaNodeKindType, type SagaNodeStatus as SagaNodeStatusType } from '@/dtos/SagaWorkflow'
import { Box, Flex, Text } from '@chakra-ui/react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { Handle, Position, type Node, type NodeProps } from '@xyflow/react'
import { sagaNodeKindIcons } from './kindVisuals'
import { NODE_HEIGHT, NODE_WIDTH } from './layout'

// A `type` (not `interface`) so it satisfies React Flow's `Record<string, unknown>` data constraint without an index signature.
export type StateNodeData = {
  label: string
  kind: SagaNodeKindType
  status: SagaNodeStatusType
  current: boolean
}

export type StateFlowNode = Node<StateNodeData, 'state'>

// One saga state, coloured by its run status and prefixed with its kind glyph. Handles are hidden (read-only
// diagram) but required so edges attach.
export const StateNode = ({ data }: NodeProps<StateFlowNode>) => {
  const scheme = sagaNodeStatusColor[data.status]
  const active = data.status === SagaNodeStatus.Active
  const icons = sagaNodeKindIcons(data.kind)

  return (
    <Box
      w={`${NODE_WIDTH}px`}
      minH={`${NODE_HEIGHT}px`}
      px={3}
      py={2}
      borderWidth={active ? '2px' : '1px'}
      borderColor={`${scheme}.400`}
      bg={`${scheme}.50`}
      borderRadius="md"
      boxShadow={active ? 'md' : 'sm'}
      display="flex"
      alignItems="center"
      justifyContent="center"
    >
      {/* Left/Right carry the cross-column backbone; Top/Bottom let same-column edges enter/leave vertically so they
          route through the inter-lane gap instead of sweeping back across the node body. All hidden (read-only). */}
      <Handle id="l" type="target" position={Position.Left} style={{ opacity: 0 }} isConnectable={false} />
      <Handle id="tt" type="target" position={Position.Top} style={{ opacity: 0 }} isConnectable={false} />
      <Handle id="ts" type="source" position={Position.Top} style={{ opacity: 0 }} isConnectable={false} />
      <Handle id="bt" type="target" position={Position.Bottom} style={{ opacity: 0 }} isConnectable={false} />
      <Handle id="bs" type="source" position={Position.Bottom} style={{ opacity: 0 }} isConnectable={false} />
      <Flex align="center" gap={2} minW={0}>
        {icons.length > 0 && (
          <Flex color={`${scheme}.500`} flexShrink={0} gap={1}>
            {icons.map((ic, i) => <FontAwesomeIcon key={i} icon={ic} />)}
          </Flex>
        )}
        <Text fontSize="sm" fontWeight={active ? 'bold' : 'medium'} color={`${scheme}.800`} noOfLines={2}>
          {data.label}
        </Text>
      </Flex>
      <Handle id="r" type="source" position={Position.Right} style={{ opacity: 0 }} isConnectable={false} />
    </Box>
  )
}
